import assign from 'lodash/assign';
import STATS_SOURCES from '../ot/peer_connection/statsSources';

const createActorStatsHelper = (deps = {}) => {
  const { isPublisher, processStats } = deps;
  let correction;
  const fieldsToCorrect = isPublisher ?
    {
      video: ['bytesSent', 'packetsSent', 'packetsLost'],
      audio: ['bytesSent', 'packetsSent', 'packetsLost'],
    } :
    {
      video: ['bytesReceived', 'packetsReceived', 'packetsLost', 'freezeCount', 'pauseCount', 'totalFreezesDuration', 'totalPausesDuration'],
      audio: ['bytesReceived', 'packetsReceived', 'packetsLost'],
    };

  const getStats = pc => new Promise((resolve, reject) => {
    if (!pc) {
      resolve({
        error: new Error('No peer connection'),
      });
      return;
    }
    pc.getStats(STATS_SOURCES.SDK, (error, stats) => {
      if (error) {
        resolve({ error });
        return;
      }
      if (!processStats && !pc.processStats) {
        reject(new Error('No processStats function available'));
        return;
      }
      try {
        const processedStats = processStats ? processStats(stats) : pc.processStats(stats);
        resolve({ stats: processedStats });
      } catch (e) {
        reject(e);
      }
    });
  });

  const addStats = (totalStats, stats) => {
    if (!stats || (!stats.audio && !stats.video)) {
      return totalStats;
    }
    const newTotalStats = {
      ...totalStats,
    };
    ['audio', 'video'].forEach((mediaType) => {
      if (totalStats?.[mediaType] || stats?.[mediaType]) {
        newTotalStats[mediaType] = { ...totalStats?.[mediaType] };
        fieldsToCorrect[mediaType].forEach((key) => {
          if (stats[mediaType]?.[key]) {
            if (!newTotalStats[mediaType][key]) {
              newTotalStats[mediaType][key] = stats[mediaType][key];
            } else {
              newTotalStats[mediaType][key] += stats[mediaType][key];
            }
          }
        });
      }
    });
    return newTotalStats;
  };

  const actorStatsHelper = {
    removePeerConnection: async (pc) => {
      try {
        const { stats } = await getStats(pc);
        correction = addStats(correction, stats);
      } catch (e) {
        // ignore
      }
    },
    getStats: async (pcsWithMeta, activeSourceStreamId) => {
      const statsPromises = pcsWithMeta.map(async ({ pc }) => getStats(pc));
      const allStats = await Promise.all(statsPromises);
      const statsAndPCsWithMeta = pcsWithMeta.map((item, index) => ({
        ...item,
        ...allStats[index],
      }));
      const activeStatsAndPCWithMeta =
        statsAndPCsWithMeta.find(({ pc }) => pc.getSourceStreamId() === activeSourceStreamId) ||
        statsAndPCsWithMeta[0];
      if (!activeStatsAndPCWithMeta) {
        return null;
      }
      if (activeStatsAndPCWithMeta.error) {
        throw activeStatsAndPCWithMeta.error;
      }
      let totalStats = addStats(activeStatsAndPCWithMeta.stats, correction);
      totalStats = statsAndPCsWithMeta.reduce((accStats, { pc, stats }) => {
        if (pc === activeStatsAndPCWithMeta.pc) {
          return accStats;
        }
        return addStats(accStats, stats);
      }, totalStats);
      if (!isPublisher) {
        return totalStats;
      }
      const { remoteConnectionId, remoteSubscriberId } = activeStatsAndPCWithMeta.metadata || {};
      return assign(
        remoteConnectionId?.match(/^symphony\./) ? {} : {
          subscriberId: remoteSubscriberId,
          connectionId: remoteConnectionId,
        },
        { stats: totalStats }
      );
    },
  };

  return actorStatsHelper;
};

export default createActorStatsHelper;
